/**
 * The Help Desk's conversation (CLOSEOUT-ESTIMATE-INTEL §D3/§D4; one panel
 * since #1703).
 *
 * It used to be a second, differently styled "Help" drawer: asking the Help
 * Desk a question closed the Help Desk and opened the drawer, which asked
 * "What do you need?" again with the question sitting unsent (2026-10-01 test
 * drive). Now the conversation lives inside the top-bar Help Desk panel:
 * `useSupportCopilotChat` holds it (mounted in the top bar, so it survives
 * close/reopen) and `SupportCopilotThread` renders it. A question typed into
 * the Help Desk is sent straight away.
 *
 * Opening without a question is free: a scripted opener + tap chips, zero
 * tokens until the person says something that needs the AI.
 *
 * Office only (support.flag) — renders nothing for techs, and every endpoint
 * 403s them regardless.
 */
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Paperclip, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { capturePageContext, describePageContext, type CapturedPageContext } from "@/lib/page-context";
import { PERMISSIONS } from "@/lib/permissions";
import { HELP_DESK_CLOSE_EVENT, HELP_DESK_OPEN_EVENT } from "@/lib/use-job-checkup";
import {
    collectScreenControls,
    collectScreenText,
    recentScreenErrors,
    showMeOnPage,
    startErrorWatch,
} from "@/lib/help-desk-screen";
import { HelpDeskActionCard, actionNote, type ActionCard } from "@/components/help-desk-action-card";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ */
/*  Open bus                                                           */
/* ------------------------------------------------------------------ */

/** Open the Help Desk from anywhere (palette, inline links). With
 *  `initialText`, that question is asked straight away (#1703). */
export function openSupportCopilot(initialText?: string): void {
    const text = initialText?.trim();
    window.dispatchEvent(new CustomEvent(HELP_DESK_OPEN_EVENT, { detail: text ? { text } : undefined }));
}

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ChatMessage {
    role: "user" | "assistant";
    content: string;
    /** Scripted (zero-token) messages are rendered the same but never sent
     * as assistant turns came from the model — they're context the model
     * re-reads as part of the transcript. */
    scripted?: boolean;
    /** "Show me" target: a button label the answer points at (#1947). */
    show?: string | null;
    /** One-tap fix cards this answer drew (#1947 Phase 2). */
    actions?: ActionCard[];
}

interface PendingAttachment {
    fileKey: string;
    fileName: string;
    mimeType: string;
    sizeBytes: number;
}

interface ChatResponse {
    ok?: boolean;
    error?: "BUDGET_EXHAUSTED" | "TURN_CAP";
    message?: string;
    text?: string;
    chips?: string[];
    /** A button on the person's screen the answer points at → "Show me". */
    show?: string | null;
    finding_ids?: string[];
    actions?: ActionCard[];
    /** What it looked up this chat; sent back with the next question (#2178). */
    memory?: MemoryItem[];
}

/** One look-up the Help Desk did earlier in this chat (#2178). Kept here,
 *  with the conversation, so a follow-up question doesn't start blind. */
interface MemoryItem {
    tool: string;
    input: string;
    result: string;
}

interface StatusResponse {
    ai_available: boolean;
    budget_usd: number;
    spent_today_usd: number;
    max_turns: number;
}

/** Opening tap-chips (spec §D3): the five ways a conversation starts. */
const OPENING_CHIPS = ["How do I…?", "Something's wrong", "Wrong words", "Wrong number", "Missing info"];

/** Scripted (free) follow-ups for the first tap — the AI only comes in once
 * the user has typed their actual comment, so a clean flag costs one turn. */
const SCRIPTED_FOLLOWUPS: Record<string, string> = {
    "How do I…?": "What are you trying to do? I'll walk you through it step by step.",
    "Something's wrong": "Tell me what looks wrong — one sentence is plenty. I'll check the data behind it.",
    "Wrong words": "Which text is wrong, and what should it say?",
    "Wrong number": "Which number looks wrong, and what did you expect?",
    "Missing info": "What's missing, and where did you expect to see it?",
};

function newConversationId(): string {
    return crypto.randomUUID();
}

/* ------------------------------------------------------------------ */
/*  The conversation                                                   */
/* ------------------------------------------------------------------ */

export interface SupportCopilotChat {
    allowed: boolean;
    /** A conversation is under way (anything in the transcript). */
    active: boolean;
    messages: ChatMessage[];
    chips: string[];
    draft: string;
    setDraft: (v: string) => void;
    busy: boolean;
    aiStopped: null | { reason: "budget" | "turns"; message: string };
    flagMode: boolean;
    setFlagMode: (v: boolean) => void;
    lastFindingId: string | null;
    attachments: PendingAttachment[];
    pageCtx: CapturedPageContext | null;
    /** Begin (or continue) a conversation. With text, it's sent now. */
    start: (text?: string) => void;
    submit: (text: string) => void;
    reset: () => void;
    attach: (file: File) => Promise<void>;
    flagDone: (findingId: string) => void;
    /** A fix card changed (Do it, Undo, Cancel): redraw it in place. */
    updateAction: (card: ActionCard) => void;
}

export function useSupportCopilotChat(): SupportCopilotChat {
    const { user, can } = useAuth();
    const allowed = !!user && can(PERMISSIONS.support.flag);
    const canDiagnose = !!user && can(PERMISSIONS.support.diagnose);

    const [conversationId, setConversationId] = useState(newConversationId);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [chips, setChips] = useState<string[]>([]);
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);
    const [pageCtx, setPageCtx] = useState<CapturedPageContext | null>(null);
    const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
    const [aiStopped, setAiStopped] = useState<null | { reason: "budget" | "turns"; message: string }>(null);
    const [flagMode, setFlagMode] = useState(false);
    const [lastFindingId, setLastFindingId] = useState<string | null>(null);
    const [memory, setMemory] = useState<MemoryItem[]>([]);
    const active = messages.length > 0;

    // Note the app's error messages as they appear, so "it gives an error"
    // reaches the Help Desk with the error's words (#2178).
    useEffect(() => (allowed ? startErrorWatch() : undefined), [allowed]);

    const statusQ = useQuery({
        queryKey: ["support-copilot", "status"],
        queryFn: () => api.get<StatusResponse>("/api/support-copilot/status"),
        enabled: allowed && active,
        staleTime: 60_000,
        retry: false,
    });
    const aiAvailable = canDiagnose && (statusQ.data?.ai_available ?? true) && aiStopped == null;

    const sendToServer = useCallback(
        async (nextMessages: ChatMessage[], ctx: CapturedPageContext | null) => {
            setBusy(true);
            setChips([]);
            try {
                const resp = await api.post<ChatResponse>("/api/support-copilot/chat", {
                    conversationId,
                    // A fix card's outcome rides on its answer, so the Help Desk
                    // knows what was done when it reads the conversation back.
                    messages: nextMessages.map((m) => ({
                        role: m.role,
                        content:
                            m.actions && m.actions.length > 0
                                ? `${m.content}\n${m.actions.map(actionNote).join("\n")}`
                                : m.content,
                    })),
                    route: ctx?.route ?? null,
                    pageContext: ctx
                        ? {
                              page: ctx.page,
                              entity: ctx.entity,
                              query: ctx.query,
                              snapshot: ctx.snapshot,
                              // What's on screen right now (#1947): button and tab labels.
                              screen: collectScreenControls(),
                              // The words on screen and the errors shown (#2178).
                              screenText: collectScreenText(),
                              errors: recentScreenErrors(),
                          }
                        : null,
                    memory,
                    attachments:
                        attachments.length > 0
                            ? attachments.map((a) => ({
                                  fileKey: a.fileKey,
                                  mimeType: a.mimeType,
                                  fileName: a.fileName,
                              }))
                            : undefined,
                });
                setAttachments([]);
                if (resp.error === "BUDGET_EXHAUSTED" || resp.error === "TURN_CAP") {
                    setAiStopped({
                        reason: resp.error === "BUDGET_EXHAUSTED" ? "budget" : "turns",
                        message: resp.message ?? "The Help Desk can't answer right now.",
                    });
                    setFlagMode(true);
                    return;
                }
                if (Array.isArray(resp.memory)) setMemory(resp.memory);
                if (resp.text) {
                    setMessages((prev) => [
                        ...prev,
                        {
                            role: "assistant",
                            content: resp.text ?? "",
                            show: resp.show ?? null,
                            actions: resp.actions && resp.actions.length > 0 ? resp.actions : undefined,
                        },
                    ]);
                    setChips(resp.chips ?? []);
                }
                if (resp.finding_ids && resp.finding_ids.length > 0) {
                    setLastFindingId(resp.finding_ids[resp.finding_ids.length - 1]);
                }
            } catch (e) {
                toast.error(e instanceof Error ? e.message : "The Help Desk didn't answer — try again");
                setChips(["Try again", "Flag it without AI"]);
            } finally {
                setBusy(false);
            }
        },
        [attachments, conversationId, memory],
    );

    const send = useCallback(
        (prior: ChatMessage[], content: string, ctx: CapturedPageContext | null) => {
            const trimmed = content.trim();
            if (trimmed.length === 0 || busy) return;
            setDraft("");
            const userMsg: ChatMessage = { role: "user", content: trimmed };
            // First tap on an opening chip gets a scripted (free) follow-up.
            const priorUserTurns = prior.filter((m) => m.role === "user").length;
            if (OPENING_CHIPS.includes(trimmed) && priorUserTurns === 0 && SCRIPTED_FOLLOWUPS[trimmed]) {
                setMessages([
                    ...prior,
                    userMsg,
                    { role: "assistant", scripted: true, content: SCRIPTED_FOLLOWUPS[trimmed] },
                ]);
                setChips([]);
                return;
            }
            const next = [...prior, userMsg];
            setMessages(next);
            if (!aiAvailable) {
                setFlagMode(true);
                return;
            }
            void sendToServer(next, ctx);
        },
        [aiAvailable, busy, sendToServer],
    );

    const start = useCallback(
        (text?: string) => {
            if (!allowed) return;
            const t = text?.trim() ?? "";
            if (active) {
                if (t) send(messages, t, pageCtx);
                return;
            }
            const ctx = capturePageContext();
            setPageCtx(ctx);
            if (t) {
                send([], t, ctx);
                return;
            }
            setMessages([
                {
                    role: "assistant",
                    scripted: true,
                    content: `Hey${user?.display_name ? ` ${user.display_name.split(" ")[0]}` : ""} — you're on ${describePageContext(ctx)}. What do you need?`,
                },
            ]);
            setChips(OPENING_CHIPS);
        },
        [active, allowed, messages, pageCtx, send, user?.display_name],
    );

    const submit = useCallback((text: string) => send(messages, text, pageCtx), [messages, pageCtx, send]);

    const reset = useCallback(() => {
        setConversationId(newConversationId());
        setMessages([]);
        setChips([]);
        setDraft("");
        setAttachments([]);
        setAiStopped(null);
        setFlagMode(false);
        setLastFindingId(null);
        setPageCtx(null);
        setMemory([]);
    }, []);

    const attach = useCallback(async (file: File) => {
        const form = new FormData();
        form.append("file", file);
        try {
            const resp = await api.postForm<{
                file_key: string;
                file_name: string;
                mime_type: string;
                size_bytes: number;
            }>("/api/support-copilot/uploads", form);
            setAttachments((prev) => [
                ...prev,
                {
                    fileKey: resp.file_key,
                    fileName: resp.file_name,
                    mimeType: resp.mime_type,
                    sizeBytes: resp.size_bytes,
                },
            ]);
            toast.success(`Attached ${resp.file_name}`);
        } catch (e) {
            toast.error(e instanceof Error ? e.message : "Upload failed (10 MB max, images/PDF only)");
        }
    }, []);

    const flagDone = useCallback((findingId: string) => {
        setFlagMode(false);
        setLastFindingId(findingId);
        setAttachments([]);
    }, []);

    const updateAction = useCallback((card: ActionCard) => {
        setMessages((prev) =>
            prev.map((m) =>
                m.actions?.some((a) => a.id === card.id)
                    ? { ...m, actions: m.actions.map((a) => (a.id === card.id ? card : a)) }
                    : m,
            ),
        );
    }, []);

    return {
        allowed,
        active,
        messages,
        chips,
        draft,
        setDraft,
        busy,
        aiStopped,
        flagMode,
        setFlagMode,
        lastFindingId,
        attachments,
        pageCtx,
        start,
        submit,
        reset,
        attach,
        flagDone,
        updateAction,
    };
}

/* ------------------------------------------------------------------ */
/*  An answer, drawn the way the office scans it (#1947 Phase 1)        */
/* ------------------------------------------------------------------ */

/** "**Edit**" → a button-name chip; everything else stays plain text. */
function Inline({ text }: { text: string }) {
    const parts = text.split(/(\*\*[^*]+\*\*)/g).filter((p) => p.length > 0);
    return (
        <>
            {parts.map((p, i) =>
                p.startsWith("**") && p.endsWith("**") ? (
                    <span key={i} className="hbtn">
                        {p.slice(2, -2)}
                    </span>
                ) : (
                    <span key={i}>{p}</span>
                ),
            )}
        </>
    );
}

/**
 * Numbered lines become step circles, "From the manual: …" becomes a small
 * source line, blank lines separate paragraphs. Built as React nodes, never
 * as HTML, so nothing the model writes can inject markup.
 */
export function AnswerText({ text }: { text: string }) {
    const blocks: Array<{ kind: "p" | "steps" | "src"; lines: string[] }> = [];
    for (const raw of text.split("\n")) {
        const line = raw.trimEnd();
        const step = /^\s*\d+[.)]\s+(.*)$/.exec(line);
        if (step) {
            const prev = blocks[blocks.length - 1];
            if (prev?.kind === "steps") prev.lines.push(step[1]);
            else blocks.push({ kind: "steps", lines: [step[1]] });
        } else if (/^from the manual:/i.test(line.trim())) {
            blocks.push({ kind: "src", lines: [line.trim()] });
        } else if (line.trim().length === 0) {
            blocks.push({ kind: "p", lines: [] });
        } else {
            const prev = blocks[blocks.length - 1];
            if (prev?.kind === "p" && prev.lines.length > 0) prev.lines.push(line);
            else blocks.push({ kind: "p", lines: [line] });
        }
    }
    return (
        <>
            {blocks
                .filter((b) => b.lines.length > 0)
                .map((b, i) =>
                    b.kind === "steps" ? (
                        <ol key={i} className="hsteps">
                            {b.lines.map((l, j) => (
                                <li key={j}>
                                    <span>
                                        <Inline text={l} />
                                    </span>
                                </li>
                            ))}
                        </ol>
                    ) : b.kind === "src" ? (
                        <div key={i} className="hsrc">
                            {b.lines[0]}
                        </div>
                    ) : (
                        <p key={i} className="m-0 whitespace-pre-wrap" style={{ marginTop: i > 0 ? 6 : 0 }}>
                            <Inline text={b.lines.join("\n")} />
                        </p>
                    ),
                )}
        </>
    );
}

/* ------------------------------------------------------------------ */
/*  The conversation on screen (inside the Help Desk panel)            */
/* ------------------------------------------------------------------ */

export function SupportCopilotThread({ chat }: { chat: SupportCopilotChat }) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        const el = scrollRef.current;
        // jsdom has no scrollTo; guard for tests.
        if (el && typeof el.scrollTo === "function") el.scrollTo({ top: el.scrollHeight });
    }, [chat.messages, chat.busy]);
    if (!chat.allowed || !chat.active) return null;
    const last = chat.messages[chat.messages.length - 1];
    const showTarget = last?.role === "assistant" ? (last.show ?? null) : null;

    return (
        <div data-testid="help-desk-conversation" className="flex min-h-0 flex-1 flex-col">
            <div ref={scrollRef} className="hbody">
                {chat.messages.map((m, i) =>
                    m.role === "user" ? (
                        <div key={i} className="hb whitespace-pre-wrap you">
                            {m.content}
                        </div>
                    ) : (
                        <Fragment key={i}>
                            <div className="hb gw">
                                <AnswerText text={m.content} />
                            </div>
                            {m.actions?.map((card) => (
                                <HelpDeskActionCard
                                    key={card.id}
                                    card={card}
                                    onChange={chat.updateAction}
                                    onNext={i === chat.messages.length - 1 ? chat.submit : undefined}
                                />
                            ))}
                        </Fragment>
                    ),
                )}
                {chat.busy && (
                    <div className="flex items-center gap-2 text-xs text-[#6B6560]">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…
                    </div>
                )}
                {chat.aiStopped && (
                    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs leading-snug">
                        {chat.aiStopped.message}
                    </div>
                )}
                {chat.lastFindingId && (
                    <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs leading-snug">
                        Sent on.
                    </div>
                )}
            </div>

            {(chat.chips.length > 0 || showTarget) && !chat.busy && (
                <div className="hchips">
                    {showTarget && (
                        <button
                            type="button"
                            className="hchip hshow"
                            onClick={() => {
                                // On a phone the panel covers the page: get it out of the way first.
                                if (window.innerWidth < 768)
                                    window.dispatchEvent(new CustomEvent(HELP_DESK_CLOSE_EVENT));
                                const found = showMeOnPage(showTarget);
                                if (!found) toast.message("I can't see that button on this screen right now.");
                            }}
                        >
                            ◎ Show me
                        </button>
                    )}
                    {chat.chips.map((chip) => (
                        <button
                            key={chip}
                            type="button"
                            className="hchip"
                            onClick={() => (chip === "Flag it without AI" ? chat.setFlagMode(true) : chat.submit(chip))}
                        >
                            {chip}
                        </button>
                    ))}
                </div>
            )}

            {chat.flagMode ? (
                <PlainFlagForm
                    pageCtx={chat.pageCtx}
                    transcript={chat.messages}
                    attachments={chat.attachments}
                    onDone={chat.flagDone}
                    onCancel={() => chat.setFlagMode(false)}
                />
            ) : (
                <form
                    className="hask"
                    onSubmit={(e) => {
                        e.preventDefault();
                        chat.submit(chat.draft);
                    }}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
                        className="hidden"
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) void chat.attach(f);
                            e.target.value = "";
                        }}
                    />
                    <button
                        type="button"
                        aria-label="Attach file"
                        className="hck-alt"
                        title={chat.attachments.map((a) => a.fileName).join(", ") || "Attach a screenshot or PDF"}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <Paperclip size={15} />
                        {chat.attachments.length > 0 ? ` ${chat.attachments.length}` : ""}
                    </button>
                    <input
                        aria-label="Message"
                        placeholder="Reply…"
                        value={chat.draft}
                        onChange={(e) => chat.setDraft(e.target.value)}
                    />
                    <button
                        type="submit"
                        className="go"
                        aria-label="Send"
                        disabled={chat.busy || chat.draft.trim().length === 0}
                    >
                        <Send size={15} />
                    </button>
                </form>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Plain flag form — works with zero AI budget                         */
/* ------------------------------------------------------------------ */

function PlainFlagForm({
    pageCtx,
    transcript,
    attachments,
    onDone,
    onCancel,
}: {
    pageCtx: CapturedPageContext | null;
    transcript: ChatMessage[];
    attachments: PendingAttachment[];
    onDone: (findingId: string) => void;
    onCancel: () => void;
}) {
    const [title, setTitle] = useState("");
    const [findingType, setFindingType] = useState<"bug" | "friction" | "idea" | "data_fix">("bug");
    const [detail, setDetail] = useState("");
    // Owner alerts ring on critical/high only (services/findingAlert.ts). This
    // form sent no severity at all, so the server defaulted every flag to
    // "medium" and NOTHING filed here could reach the owner (#1010) — worst on
    // the day the AI budget runs out, since that is exactly when the drawer
    // falls back to this form. One box, off by default, is the whole fix.
    const [urgent, setUrgent] = useState(false);
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (title.trim().length === 0 || saving) return;
        setSaving(true);
        try {
            const resp = await api.post<{ finding_id: string }>("/api/support-copilot/findings", {
                title: title.trim(),
                findingType,
                // "high", not "critical" — critical stays the copilot's own
                // tool-verified diagnosis so a self-report can't outrank it on
                // the board's impact ranking.
                severity: urgent ? "high" : "medium",
                route: pageCtx?.route ?? null,
                pageContext: pageCtx
                    ? { page: pageCtx.page, entity: pageCtx.entity, query: pageCtx.query, snapshot: pageCtx.snapshot }
                    : null,
                entityRefs: pageCtx?.entity ? { [`${pageCtx.entity.type}_id`]: pageCtx.entity.id } : null,
                reproduction: detail.trim() || null,
                transcript: transcript.map((m) => ({ role: m.role, content: m.content })),
                attachmentKeys:
                    attachments.length > 0
                        ? attachments.map((a) => ({
                              fileKey: a.fileKey,
                              fileName: a.fileName,
                              mimeType: a.mimeType,
                              sizeBytes: a.sizeBytes,
                          }))
                        : undefined,
            });
            toast.success("Sent. You'll get a notification when it's fixed.");
            onDone(resp.finding_id);
        } catch (e) {
            toast.error(e instanceof Error ? e.message : "Failed to save flag");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="border-t px-3 py-3 space-y-2" data-testid="plain-flag-form">
            <p className="text-xs font-medium text-muted-foreground">
                Send it to be fixed. You get a notification when it is.
            </p>
            <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="One-line summary"
                aria-label="Flag title"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <div className="flex gap-1.5">
                {(
                    [
                        ["bug", "Bug"],
                        ["friction", "Confusing"],
                        ["idea", "Idea"],
                        ["data_fix", "Data fix"],
                    ] as const
                ).map(([value, label]) => (
                    <button
                        key={value}
                        type="button"
                        onClick={() => setFindingType(value)}
                        className={cn(
                            "rounded-full border px-2.5 py-1 text-xs transition-colors",
                            findingType === value
                                ? "border-primary bg-primary/10 text-primary font-medium"
                                : "border-border text-foreground/70 hover:bg-muted",
                        )}
                    >
                        {label}
                    </button>
                ))}
            </div>
            {/* Says who sees this, which nothing in the UI did before. Never
                names a person — the platform has to host other companies. */}
            <label className="flex items-start gap-2 text-xs text-foreground/80 cursor-pointer">
                <input
                    type="checkbox"
                    checked={urgent}
                    onChange={(e) => setUrgent(e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 rounded border-border accent-primary"
                />
                <span>
                    <span className="font-medium text-foreground">Urgent — notify the owner now</span>
                    <span className="block text-foreground/70">
                        Leave this off for anything that can wait for the daily summary.
                    </span>
                </span>
            </label>
            <textarea
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                rows={2}
                placeholder="What happened / what you expected (optional)"
                aria-label="Flag details"
                className="w-full resize-none rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <div className="flex justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="rounded-md px-3 py-1.5 text-xs text-foreground/70 hover:bg-muted"
                >
                    Back
                </button>
                <button
                    type="button"
                    onClick={() => void submit()}
                    disabled={saving || title.trim().length === 0}
                    className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-40"
                >
                    {saving ? "Saving…" : "Flag it"}
                </button>
            </div>
        </div>
    );
}
